import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import SearchModel from 'coreClasses/search/SearchModel';
import {useHistory, useLocalesMgr, useSearchMgrInstance, useDebouncedValue, useLocation, useLocalStorage} from 'coreHooks';
import {ESearchType} from 'coreClasses/search/ISearch';
import useChangeEffect from 'coreHooks/useChangeEffect';

import {ISearchFormRef} from 'brandComponents/SearchForm/ISearchForm';
import {HISTORY_CONFIG, QUERY_MIN_LENGTH} from 'brandConstants';

type IUseSearchUtilsProps = {
	searchInstanceKey?: string;
	searchType?: ESearchType;
	limit?: number;
	enableSuggestions?: boolean;
	enablePopularSearches?: boolean;
	updateUrl?: boolean;
};

export const useSearchUtils = (data: IUseSearchUtilsProps = {}) => {
	const {
		searchInstanceKey,
		searchType,
		limit,
		updateUrl = false,
		enableSuggestions = false,
		enablePopularSearches = false
	} = data;

	const [firstRender, setFirstRender] = useState(true);
	const [suggests, setSuggests] = useState([]);
	const [selectedSuggest, setSelectedSuggest] = useState('');
	const [popularSearches, setPopularSearches] = useState([]);
	const searchFormRef = useRef<ISearchFormRef>(null);
	const location = useLocation();
	const searchQueryURL = new URLSearchParams(location.search).get('q');
	const history = useHistory();
	const localesMgr = useLocalesMgr();
	const searchMgr = useSearchMgrInstance({
		key: searchInstanceKey,
		searchType: searchType,
		compare: ['query'],
	});
	const searchModel: SearchModel = searchMgr.getStoredSearchModel();
	const selectedRefinements = searchModel?.getSelectedRefinements();
	const isCategoryPage = !!selectedRefinements?.cgid;

	const defaultQuery = (searchQueryURL || searchModel?.getSearchQuery() || '').replaceAll('*', '');
	const [_query, setQuery] = useState(defaultQuery);

	const [isLoading, setIsLoading] = useState(false);

	const query = useDebouncedValue<string>(_query, 500);

	const hasSearchQuery = query?.length >= QUERY_MIN_LENGTH;
	const searchRedirectUrl = hasSearchQuery ? searchModel?.getSearchRedirectUrl() || '' : '';
	const totalProducts = hasSearchQuery ? searchModel?.getTotalProductsNumber() : 0;
	const hasResults = (hasSearchQuery && totalProducts > 0) || searchModel?.getSearchRedirectUrl();
	const noResults = !hasResults;
	const currentLanguage = localesMgr.getCurrentLanguage();

	const loadingQueryRef = useRef<string | null>(null);

	useLayoutEffect(() => {
		// Not for the same query again: a PLP kept alive (`<Activity>`) shown again runs its effects again.
		if (hasSearchQuery && loadingQueryRef.current !== query) {
			setIsLoading(true);
		}

		loadingQueryRef.current = query;
	}, [query]);

	const handleQuerySearch = useCallback(
		() => {
			if (searchQueryURL !== query) {
				if (searchRedirectUrl) {
					history.push(searchRedirectUrl);
				} else {
					history.push(localesMgr.updateLinkWithLocale(`search?q=${query.replaceAll('*', '')}`));
				}
			}
		},
		[query, searchRedirectUrl],
	);

	const choiceSuggest = useCallback((value: string) => {
		setQuery(value);
		setSelectedSuggest(value);
		setSuggests([]);
	}, []);

	const [savedSearches, setSavedSearches] = useLocalStorage<{value:string, url:string}[]>(HISTORY_CONFIG.store_key, []);

	const saveQueryToHistory = () => {
		const redirectUrl = searchRedirectUrl || localesMgr.updateLinkWithLocale(`search?q=${query}`);
		const updatedSearches = savedSearches.filter((entry) => entry.value !== query);
		updatedSearches.unshift({value: query, url: redirectUrl});

		const trimmed = updatedSearches.slice(0, HISTORY_CONFIG.max_results);

		setSavedSearches(trimmed);
	};

	useEffect(() => {
		if (firstRender || query !== searchQueryURL) {
			setQuery(searchQueryURL);
		}
	}, [searchQueryURL]);

	useEffect(() => {
		setFirstRender(false);
	}, []);

	// Only when the query changes: not on mount, nor when a PLP kept alive is shown again (its results are there).
	useChangeEffect(() => {
		if (hasSearchQuery) {
			searchMgr.setUserSelectedSorting('');
			searchMgr.setUserSelectedRefinements({});
			searchMgr.enableWildCardQuery(true);
			setIsLoading(true);

			if (enableSuggestions && !selectedSuggest) {
				searchMgr
					.suggestions({searchQuery: query, limit: 3})
					.then(setSuggests);
			}

			searchMgr
				.search({
					searchQuery: query,
					page: 1,
					limitParam: limit,
				})
				.finally(() => {
					searchFormRef?.current?.setValue(query);
					// Timeout is necessary to prevent show noResults for a short time
					setTimeout(() => setIsLoading(false), 500);

					if (firstRender || updateUrl) {
						handleQuerySearch();
					}

					if (selectedSuggest) {
						setSelectedSuggest('');
					}
				});
		} else {
			setSuggests([]);
			searchMgr.clearStoredSearch();
		}
	}, [query]);

	useEffect(() => {
		if (!enablePopularSearches) {
			return;
		}

		searchMgr
			.popularSearches({limit: 3})
			.then(setPopularSearches);
	}, [currentLanguage]);

	return {
		query,
		setQuery,
		hasResults,
		isCategoryPage,
		isLoading,
		noResults,
		totalProducts,
		searchModel,
		searchFormRef,
		handleQuerySearch,
		suggests,
		popularSearches,
		choiceSuggest,
		queryId: searchModel?.getQueryId(),
		hasSearchQuery,
		savedSearches,
		saveQueryToHistory
	};
};
